import { MutableRefObject } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { Loading } from '@/components/common/Loading';
import AddSystemSection from './AddSystemSection';
import { CriticalSystem, CriticalSystemType } from '@/types/CriticalInfo.ts';
import SystemGroupCard from './SystemGroupCard';
import { useCriticalInfoCards } from '@/hooks/house/critical-info/useCriticalInfoCards';

/**
 * Main content component for the Critical House Information step.
 * Provides an interface for users to document and manage important systems
 * in their home, which is essential for emergency preparedness.
 */
interface CriticalInfoContentProps {
	systems: CriticalSystem[];
	isLoading: boolean;
	addSystem: (name: string, type?: CriticalSystemType) => void;
	removeSystem: (name: string) => void;
	updateSystemField: (systemName: string, fieldName: string, value: string) => void;
	systemRefs?: MutableRefObject<Record<string, HTMLDivElement | null>>;
	dirtySystemNames?: Set<string>;
	documentedSystemNames?: Set<string>;
	savingSystemName?: string | null;
	isSaving?: boolean;
	onSaveSystem?: (systemName: string) => Promise<boolean | void> | boolean | void;
	onCancelSystem?: (systemName: string) => void;
	onDoneSystem?: (systemName: string) => void;
	addingSystemName?: string | null;
	onAddAnotherSystem?: (systemName: string) => void;
	onFinishAddingSystem?: () => void;
	onRemoveSystem?: (systemName: string) => Promise<boolean | void> | boolean | void;
	collapseCancelsSystemNames?: Set<string>;
	canEdit?: boolean;
	canRemove?: boolean;
}

const CriticalInfoContent = (props: CriticalInfoContentProps) => {
	const {
		systems,
		isLoading,
		addSystem,
		removeSystem,
		updateSystemField,
		systemRefs,
		dirtySystemNames,
		documentedSystemNames,
		savingSystemName,
		isSaving,
		onSaveSystem,
		onCancelSystem,
		onDoneSystem,
		addingSystemName,
		onAddAnotherSystem,
		onFinishAddingSystem,
		onRemoveSystem,
		collapseCancelsSystemNames,
		canEdit = true,
		canRemove = true,
	} = props;
	const {
		systemGroups,
		availableSystems,
		existingSystems,
		expandedGroupSystem,
		setExpandedGroupSystem,
		handleAddSystem,
		isGroupFilled,
		setGroupRef,
	} = useCriticalInfoCards({
		systems,
		documentedSystemNames,
		systemRefs,
		canEdit,
		addSystem,
	});

	if (isLoading) {
		return <Loading text="Loading important systems..." />;
	}

	return (
		<VStack gap={6} align="stretch">
			<VStack gap={3} align="stretch">
				{systemGroups.map((group) => {
					const hasGroupData = isGroupFilled(group.systems);
					const hasMultipleInstances = group.systems.length > 1;

					return (
						<Box
							key={group.categoryName}
							w="full"
							ref={(node: HTMLDivElement | null) => setGroupRef(group.categoryName, group.systems, node)}
						>
							<SystemGroupCard
								categoryName={group.categoryName}
								systems={group.systems}
								isFilled={hasGroupData || hasMultipleInstances}
								expandedSystem={expandedGroupSystem}
								addingSystemName={addingSystemName}
								onToggleSystem={setExpandedGroupSystem}
								onStartAdding={() => onAddAnotherSystem?.(group.categoryName)}
								onFinishAdding={onFinishAddingSystem}
								onCancelEdit={(systemName) => {
									onCancelSystem?.(systemName);
									if (addingSystemName === systemName) onFinishAddingSystem?.();
								}}
								onDoneSystem={onDoneSystem}
								onRemoveSystem={onRemoveSystem ?? removeSystem}
								onUpdateField={updateSystemField}
								dirtySystemNames={dirtySystemNames}
								savingSystemName={savingSystemName}
								isSaving={isSaving}
								onSaveSystem={onSaveSystem}
								animateBodyEntry={!hasMultipleInstances}
								collapseCancelsSystemNames={collapseCancelsSystemNames}
								canEdit={canEdit}
								canRemove={canRemove}
							/>
						</Box>
					);
				})}
			</VStack>

			{canEdit && (
				<AddSystemSection
					availableSystems={availableSystems}
					existingSystems={existingSystems}
					onAddSystem={handleAddSystem}
				/>
			)}
		</VStack>
	);
};

export default CriticalInfoContent;
